import React, { useEffect, useMemo, useState } from 'react';
import { ScrollText, Save, RotateCcw, Plus, Trash2, Clock, AlertTriangle, User, Tag } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { Badge } from '../../../../components/ui/Badge';
import { Input } from '../../../../components/ui/Input';
import { loadGovernanceConfig, saveGovernanceConfig, type GovernanceConfigOverrides, type GraceLedgerEntry } from '../../../../governance';
/**
 * GraceLedgerSection — FG4 §Fase C UI (S9 Deelblok B).
 *
 * Surfaces and manages the grace ledger — NAMED, OWNED, EXPIRING
 * exceptions that temporarily degrade severity for a scope.
 *
 * Anti-bandaid principle #3 (made physical): every permissive shortcut
 * lives here with an owner, a reason, and an expiry date. No more
 * implicit `return OK` debt.
 *
 * Owns its own load/save lifecycle (additive, mirrors EnforcementSection).
 */
const DAY_MS = 24 * 60 * 60 * 1000;
function formatCountdown(untilIso: string, now: number): {
  text: string;
  cls: string;
  expired: boolean;
} {
  const until = Date.parse(untilIso);
  if (!Number.isFinite(until)) {
    return {
      text: 'Ongeldige datum',
      cls: 'text-red-600',
      expired: true
    };
  }
  const diff = until - now;
  if (diff < 0) {
    const daysAgo = Math.floor(-diff / DAY_MS);
    return {
      text: `Verlopen (${daysAgo}d geleden)`,
      cls: 'text-red-600 font-semibold',
      expired: true
    };
  }
  const days = Math.floor(diff / DAY_MS);
  if (days <= 3) {
    return {
      text: `Nog ${days} dag${days === 1 ? '' : 'en'}`,
      cls: 'text-amber-700 font-semibold',
      expired: false
    };
  }
  if (days <= 14) {
    return {
      text: `Nog ${days} dagen`,
      cls: 'text-amber-600',
      expired: false
    };
  }
  return {
    text: `Nog ${days} dagen`,
    cls: 'text-emerald-700',
    expired: false
  };
}
function defaultUntilIso(): string {
  const d = new Date(Date.now() + 30 * DAY_MS);
  return d.toISOString().slice(0, 10);
}
interface DraftEntry {
  module: string;
  family: string;
  questionId: string;
  until: string;
  reason: string;
  owner: string;
  degradeToSeverity: string; // string so the input can be empty
}
const EMPTY_DRAFT: DraftEntry = {
  module: '',
  family: '',
  questionId: '',
  until: defaultUntilIso(),
  reason: '',
  owner: '',
  degradeToSeverity: '50'
};
function scopeLabel(scope: GraceLedgerEntry['scope']): string {
  const parts: string[] = [];
  if (scope.module) parts.push(`module:${scope.module}`);
  if (scope.family) parts.push(`family:${scope.family}`);
  if (scope.questionId) parts.push(`qid:${scope.questionId}`);
  return parts.join(' · ') || '(globaal)';
}
function scopeSpecificity(scope: GraceLedgerEntry['scope']): {
  level: 'question' | 'family' | 'module' | 'global';
  cls: string;
} {
  if (scope.questionId) return {
    level: 'question',
    cls: 'bg-violet-100 text-violet-700 border-violet-200'
  };
  if (scope.family) return {
    level: 'family',
    cls: 'bg-blue-100 text-blue-700 border-blue-200'
  };
  if (scope.module) return {
    level: 'module',
    cls: 'bg-emerald-100 text-emerald-700 border-emerald-200'
  };
  return {
    level: 'global',
    cls: 'bg-slate-100 text-slate-700 border-slate-200'
  };
}
export function GraceLedgerSection() {
  const [entries, setEntries] = useState<GraceLedgerEntry[]>([]);
  const [originalEntries, setOriginalEntries] = useState<GraceLedgerEntry[]>([]);
  const [draft, setDraft] = useState<DraftEntry>(EMPTY_DRAFT);
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [now] = useState(() => Date.now());
  useEffect(() => {
    let cancelled = false;
    loadGovernanceConfig().then(cfg => {
      if (cancelled) return;
      const ledger = cfg.graceLedger ?? [];
      setEntries(ledger);
      setOriginalEntries(ledger);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const dirty = useMemo(() => JSON.stringify(entries) !== JSON.stringify(originalEntries), [entries, originalEntries]);
  const handleSave = async () => {
    const cfg = await loadGovernanceConfig();
    const nextCfg: GovernanceConfigOverrides = {
      ...cfg,
      graceLedger: entries
    };
    const ok = await saveGovernanceConfig(nextCfg);
    if (ok) {
      setOriginalEntries(entries);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  };
  const handleReset = () => setEntries(originalEntries);
  const handleRemove = (index: number) => {
    setEntries(prev => prev.filter((_, i) => i !== index));
  };
  const handleAdd = () => {
    setFormError(null);
    const scope: GraceLedgerEntry['scope'] = {};
    if (draft.module.trim()) scope.module = draft.module.trim().toUpperCase();
    if (draft.family.trim()) scope.family = draft.family.trim();
    if (draft.questionId.trim()) scope.questionId = draft.questionId.trim();
    if (!scope.module && !scope.family && !scope.questionId) {
      setFormError('Scope is verplicht: vul minstens een module, family of questionId in.');
      return;
    }
    if (!draft.reason.trim()) {
      setFormError('Reden is verplicht (anti-bandaid principe #3: zichtbare schuld).');
      return;
    }
    if (!draft.owner.trim()) {
      setFormError('Eigenaar is verplicht — elke uitzondering heeft een persoon die hem sluit.');
      return;
    }
    if (!draft.until || Number.isNaN(Date.parse(draft.until))) {
      setFormError('Vervaldatum is verplicht en moet een geldige datum zijn.');
      return;
    }
    const entry: GraceLedgerEntry = {
      scope,
      until: new Date(draft.until).toISOString(),
      reason: draft.reason.trim(),
      owner: draft.owner.trim()
    };
    const sev = Number(draft.degradeToSeverity);
    if (Number.isFinite(sev) && sev > 0 && sev <= 100) {
      entry.degradeToSeverity = sev;
    }
    setEntries(prev => [...prev, entry]);
    setDraft(EMPTY_DRAFT);
    setShowForm(false);
  };
  const sorted = useMemo(() => {
    return [...entries].map((e, idx) => ({
      entry: e,
      idx,
      until: Date.parse(e.until)
    })).sort((a, b) => a.until - b.until);
  }, [entries]);
  return <div data-id="element-4128">
      <div className="flex items-start justify-between gap-3 mb-1" data-id="element-4129">
        <div data-id="element-4130">
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2" data-id="element-4131">
            <ScrollText className="w-4 h-4 text-violet-500" data-id="element-4132" />
            Grace Ledger
            <Badge variant="info" size="sm" data-id="element-4133">
              FG4 · Fase C
            </Badge>
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-4134">
          {dirty && <>
              <Button variant="ghost" size="sm" onClick={handleReset} data-id="element-4135">
                <RotateCcw className="w-3.5 h-3.5 mr-1" data-id="element-4136" />
                Reset
              </Button>
              <Button variant="primary" size="sm" onClick={handleSave} data-id="element-4137">
                <Save className="w-3.5 h-3.5 mr-1" data-id="element-4138" />
                Opslaan
              </Button>
            </>}
          {saved && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded" data-id="element-4139">
              Opgeslagen
            </span>}
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-4" data-id="element-4140">
        Geregistreerde, eindige uitzonderingen die de severity tijdelijk
        degraderen voor een scope (module / family / questionId). Elke entry
        heeft een eigenaar, een reden én een vervaldatum — permissiveness wordt
        zichtbare, aftelbare schuld in plaats van impliciet en oneindig.
      </p>

      {/* Summary stats */}
      {entries.length > 0 && <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-4141">
          <div className="px-3 py-2 rounded-lg border border-slate-200 bg-slate-50" data-id="element-4142">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-600" data-id="element-4143">
              Totaal
            </p>
            <p className="text-lg font-bold text-slate-800 leading-tight" data-id="element-4144">
              {entries.length}
            </p>
          </div>
          <div className="px-3 py-2 rounded-lg border border-amber-200 bg-amber-50" data-id="element-4145">
            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700" data-id="element-4146">
              Verloopt &lt; 7d
            </p>
            <p className="text-lg font-bold text-amber-800 leading-tight" data-id="element-4147">
              {entries.filter(e => {
            const t = Date.parse(e.until);
            return Number.isFinite(t) && t - now < 7 * DAY_MS && t > now;
          }).length}
            </p>
          </div>
          <div className="px-3 py-2 rounded-lg border border-red-200 bg-red-50" data-id="element-4148">
            <p className="text-[10px] font-bold uppercase tracking-wider text-red-700" data-id="element-4149">
              Verlopen
            </p>
            <p className="text-lg font-bold text-red-800 leading-tight" data-id="element-4150">
              {entries.filter(e => {
            const t = Date.parse(e.until);
            return Number.isFinite(t) && t < now;
          }).length}
            </p>
          </div>
        </div>}

      {/* Add button */}
      {!showForm && <Button variant="secondary" size="sm" onClick={() => {
      setDraft({
        ...EMPTY_DRAFT,
        until: defaultUntilIso()
      });
      setShowForm(true);
    }} className="mb-4" data-id="element-4151">
          <Plus className="w-3.5 h-3.5 mr-1" data-id="element-4152" />
          Nieuwe grace toevoegen
        </Button>}

      {/* Add form */}
      {showForm && <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-4 mb-4 space-y-3" data-id="element-4153">
          <div className="flex items-center justify-between" data-id="element-4154">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-4155">
              Nieuwe grace-entry
            </h3>
            <button type="button" onClick={() => {
          setShowForm(false);
          setFormError(null);
        }} className="text-xs text-gray-500 hover:text-gray-700" data-id="element-4156">
              Annuleer
            </button>
          </div>

          <p className="text-[11px] text-gray-600 leading-relaxed" data-id="element-4157">
            Scope: vul <strong data-id="element-4158">één of meer</strong> in. Specificiteit:
            questionId &gt; family &gt; module.
          </p>

          <div className="grid grid-cols-3 gap-2" data-id="element-4159">
            <div data-id="element-4160">
              <label className="block text-[11px] font-medium text-gray-700 mb-1" data-id="element-4161">
                Module
              </label>
              <Input type="text" value={draft.module} onChange={e => setDraft({
            ...draft,
            module: e.target.value
          })} placeholder="bv. REK" data-id="element-4162" />
            </div>
            <div data-id="element-4163">
              <label className="block text-[11px] font-medium text-gray-700 mb-1" data-id="element-4164">
                Family
              </label>
              <Input type="text" value={draft.family} onChange={e => setDraft({
            ...draft,
            family: e.target.value
          })} placeholder="bv. REK-CIJF-PLUS" data-id="element-4165" />
            </div>
            <div data-id="element-4166">
              <label className="block text-[11px] font-medium text-gray-700 mb-1" data-id="element-4167">
                Question ID
              </label>
              <Input type="text" value={draft.questionId} onChange={e => setDraft({
            ...draft,
            questionId: e.target.value
          })} placeholder="bv. REK-CIJF-PLUS-001" data-id="element-4168" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2" data-id="element-4169">
            <div data-id="element-4170">
              <label className="block text-[11px] font-medium text-gray-700 mb-1" data-id="element-4171">
                Vervaldatum
              </label>
              <Input type="date" value={draft.until} onChange={e => setDraft({
            ...draft,
            until: e.target.value
          })} data-id="element-4172" />
            </div>
            <div data-id="element-4173">
              <label className="block text-[11px] font-medium text-gray-700 mb-1" data-id="element-4174">
                Degradeer severity naar
              </label>
              <Input type="number" min={1} max={100} value={draft.degradeToSeverity} onChange={e => setDraft({
            ...draft,
            degradeToSeverity: e.target.value
          })} placeholder="50" data-id="element-4175" />
            </div>
          </div>

          <div data-id="element-4176">
            <label className="block text-[11px] font-medium text-gray-700 mb-1" data-id="element-4177">
              Eigenaar
            </label>
            <Input type="text" value={draft.owner} onChange={e => setDraft({
          ...draft,
          owner: e.target.value
        })} placeholder="bv. content-team / @jdoe" data-id="element-4178" />
          </div>

          <div data-id="element-4179">
            <label className="block text-[11px] font-medium text-gray-700 mb-1" data-id="element-4180">
              Reden
            </label>
            <textarea value={draft.reason} onChange={e => setDraft({
          ...draft,
          reason: e.target.value
        })} placeholder="Waarom is deze uitzondering nodig en wanneer kan hij dicht?" className="w-full min-h-[60px] rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300" data-id="element-4181" />
          </div>

          {formError && <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2" data-id="element-4182">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" data-id="element-4183" />
              <p className="text-xs text-red-700" data-id="element-4184">{formError}</p>
            </div>}

          <div className="flex justify-end gap-2" data-id="element-4185">
            <Button variant="ghost" size="sm" onClick={() => {
          setShowForm(false);
          setFormError(null);
        }} data-id="element-4186">
              Annuleer
            </Button>
            <Button variant="primary" size="sm" onClick={handleAdd} data-id="element-4187">
              <Plus className="w-3.5 h-3.5 mr-1" data-id="element-4188" />
              Toevoegen aan ledger
            </Button>
          </div>
        </div>}

      {/* Entry list */}
      {entries.length === 0 && !showForm && <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/50 px-4 py-8 text-center" data-id="element-4189">
          <ScrollText className="w-6 h-6 text-gray-400 mx-auto mb-2" data-id="element-4190" />
          <p className="text-sm font-medium text-gray-700" data-id="element-4191">
            Geen grace-entries actief
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-4192">
            Permissiveness is geen default — uitzonderingen moeten expliciet
            geregistreerd worden.
          </p>
        </div>}

      <div className="space-y-2" data-id="element-4193">
        {sorted.map(({
        entry,
        idx
      }) => {
        const countdown = formatCountdown(entry.until, now);
        const spec = scopeSpecificity(entry.scope);
        return <div key={`${idx}-${entry.until}`} className={`rounded-lg border px-3 py-3 ${countdown.expired ? 'border-red-200 bg-red-50/40' : 'border-gray-100 bg-white'}`} data-id="element-4194">
              <div className="flex items-start justify-between gap-3" data-id="element-4195">
                <div className="min-w-0 flex-1 space-y-1.5" data-id="element-4196">
                  <div className="flex items-center gap-2 flex-wrap" data-id="element-4197">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${spec.cls}`} data-id="element-4198">
                      <Tag className="w-2.5 h-2.5" data-id="element-4199" />
                      {spec.level}
                    </span>
                    <span className="font-mono text-xs text-gray-800 truncate" data-id="element-4200">
                      {scopeLabel(entry.scope)}
                    </span>
                    {typeof entry.degradeToSeverity === 'number' && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 border border-amber-200" data-id="element-4201">
                        → sev {entry.degradeToSeverity}
                      </span>}
                  </div>
                  <p className="text-sm text-gray-700 leading-snug" data-id="element-4202">
                    {entry.reason}
                  </p>
                  <div className="flex items-center gap-3 text-[11px] text-gray-500" data-id="element-4203">
                    <span className="inline-flex items-center gap-1" data-id="element-4204">
                      <User className="w-3 h-3" data-id="element-4205" />
                      {entry.owner}
                    </span>
                    <span className={`inline-flex items-center gap-1 ${countdown.cls}`} data-id="element-4206">
                      <Clock className="w-3 h-3" data-id="element-4207" />
                      {countdown.text}
                    </span>
                    <span className="text-gray-400" data-id="element-4208">
                      ({new Date(entry.until).toLocaleDateString('nl-NL')})
                    </span>
                  </div>
                </div>
                <button type="button" onClick={() => handleRemove(idx)} className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50" title="Verwijder uit ledger" data-id="element-4209">
                  <Trash2 className="w-4 h-4" data-id="element-4210" />
                </button>
              </div>
            </div>;
      })}
      </div>

      <p className="text-[11px] text-gray-500 mt-4 leading-relaxed" data-id="element-4211">
        💡 Tip: verlopen entries blijven zichtbaar als een ruststand-debt signal
        — ze gelden niet meer aan de pipeline-kant (de resolver{' '}
        <span className="font-mono" data-id="element-4212">findActiveGrace</span> filtert ze al), maar
        ze moeten alsnog opgeruimd of vernieuwd worden.
      </p>
    </div>;
}